"use client";

import * as React from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { MediaPickerOverlay } from "@/media/components/MediaPickerOverlay";
import type { Asset } from "@/media/types";
import { buildAssetUrl } from "@/media/utils";
import type { BlockBase } from "@/cms/blocks/registry/types";
import type { ConfirmInvoker } from "../types";
import {
  createEmptyRichText,
  getBlockLocaleStrings,
  getLocaleUI,
  insertAtEnd,
  removeAt,
  updateAt,
  uid,
} from "../utils";
import { FaqSortableItem } from "./FaqSortableItem";
import { PlusSVG } from "@/components/ui/SVG";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export function FaqEditor({
  block,
  locale,
  onChange,
  onRequestConfirm,
}: {
  block: BlockBase;
  locale: "en" | "fa";
  onChange: (patch: (data: any) => any) => void;
  onRequestConfirm: ConfirmInvoker;
}) {
  const data = block.data as any;
  type FaqItem = { id?: string; question?: any; answer?: any };
  const items = React.useMemo<FaqItem[]>(
    () => (Array.isArray(data?.items) ? data.items : []),
    [data?.items],
  );
  const localeStrings = React.useMemo(
    () => getBlockLocaleStrings(locale),
    [locale],
  );
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
  );
  const [pickerState, setPickerState] = React.useState<{
    open: boolean;
    index: number | null;
  }>({ open: false, index: null });
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const { dir: inputDir } = getLocaleUI(locale);

  const ensureId = React.useCallback((item: any, fallback: string) => {
    if (item?.id && typeof item.id === "string") return item.id;
    return fallback;
  }, []);

  React.useEffect(() => {
    setExpanded((prev) => {
      const nextExpanded: Record<string, boolean> = {};
      items.forEach((item, idx) => {
        const key = ensureId(item, `${block.id}-faq-${idx}`);
        nextExpanded[key] = prev[key] ?? true;
      });
      return nextExpanded;
    });
  }, [items, block.id, ensureId]);

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const itemIds = React.useMemo(
    () => items.map((item, idx) => ensureId(item, `${block.id}-faq-${idx}`)),
    [items, ensureId, block.id],
  );

  const addItem = () => {
    const newItem = {
      id: uid(),
      question: { richText: createEmptyRichText() },
      answer: { richText: createEmptyRichText() },
    };
    onChange((prev) => {
      const prevItems = Array.isArray(prev.items) ? prev.items : [];
      return { ...prev, items: insertAtEnd(prevItems, newItem) };
    });
  };

  const updateItem = (index: number, patch: (item: any) => any) => {
    onChange((prev) => {
      const prevItems = Array.isArray(prev.items) ? prev.items : [];
      const current = prevItems[index] ?? {};
      return {
        ...prev,
        items: updateAt(prevItems, index, () => patch(current)),
      };
    });
  };

  const removeItem = (index: number) => {
    const commit = () =>
      onChange((prev) => {
        const prevItems = Array.isArray(prev.items) ? prev.items : [];
        return { ...prev, items: removeAt(prevItems, index) };
      });
    onRequestConfirm(
      {
        title: "Remove FAQ entry",
        description: "Are you sure you want to delete this FAQ entry?",
        confirmLabel: "Delete",
        variant: "danger",
      },
      commit,
    );
  };

  const moveItem = React.useCallback(
    (from: number, to: number) => {
      onChange((prev) => {
        const prevItems = Array.isArray(prev.items) ? prev.items : [];
        if (
          from < 0 ||
          to < 0 ||
          from >= prevItems.length ||
          to >= prevItems.length
        ) {
          return prev;
        }
        return { ...prev, items: arrayMove(prevItems, from, to) };
      });
    },
    [onChange],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = itemIds.indexOf(String(active.id));
      const newIndex = itemIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      moveItem(oldIndex, newIndex);
    },
    [itemIds, moveItem],
  );

  const openImagePicker = (index: number) =>
    setPickerState({ open: true, index });

  const closeImagePicker = () => setPickerState({ open: false, index: null });

  const applyImage = (asset: Asset) => {
    if (pickerState.index === null) {
      closeImagePicker();
      return;
    }
    const previewUrl = buildAssetUrl(asset);
    updateItem(pickerState.index, (item) => ({
      ...item,
      image: {
        ...(item.image ?? {}),
        assetId: asset.id,
        url: undefined,
        previewUrl,
        sourceName: asset.originalFilename,
        width: asset.width ?? item.image?.width,
        height: asset.height ?? item.image?.height,
      },
    }));
    closeImagePicker();
  };

  const clearImage = (index: number) => {
    updateItem(index, (item) => ({
      ...item,
      image: undefined,
    }));
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item, index) => {
              const key =
                itemIds[index] ?? ensureId(item, `${block.id}-faq-${index}`);
              return (
                <FaqSortableItem
                  key={key}
                  id={key}
                  item={item}
                  index={index}
                  isDesktop={isDesktop}
                  inputDir={inputDir}
                  localeStrings={localeStrings}
                  isOpen={expanded[key] ?? true}
                  onToggle={() => toggleExpanded(key)}
                  onRemove={() => removeItem(index)}
                  onOpenImagePicker={() => openImagePicker(index)}
                  onClearImage={() => clearImage(index)}
                  onUpdateItem={(patch) => updateItem(index, patch)}
                />
              );
            })}
          </SortableContext>
        </DndContext>
        {items.length === 0 && (
          <div className="rounded border border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
            {localeStrings.helpers.faqEmpty}
          </div>
        )}
      </div>
      <div className="flex items-center">
        <button
          type="button"
          className="flex gap-1 cursor-pointer text-sm hover:opacity-80"
          onClick={addItem}
        >
          <PlusSVG
            className="bg-slateCustom h-4 w-4 rounded p-1"
            svgColor="white"
          />
          {localeStrings.helpers.addFaq}
        </button>
      </div>
      <MediaPickerOverlay
        open={pickerState.open}
        onClose={closeImagePicker}
        onSelect={applyImage}
        accept={["image/*"]}
        usage={{ context: "cms-faq-image" }}
        title={locale === "fa" ? "انتخاب تصویر سوال" : "Select FAQ image"}
        description={
          locale === "fa"
            ? "یک تصویر برای این سوال انتخاب کنید."
            : "Choose an image for this FAQ entry."
        }
      />
    </div>
  );
}
